import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import CustomSelect from '../../Components/customs/dropdown';
/* ⚠️ One line you may need to retarget: import BASE_URL from the same module
   your other pages use (the one GetAPIcalls resolves against). */
const BASE_URL = process.env.REACT_APP_BASE_URL;
/* ================================================================== */
/*  Certificates — table of ratified customer certificates             */
/*                                                                     */
/*  Columns: Certificate ID · Customer Name · Issue Date · Expiry ·    */
/*  Action (View) — with pagination (10/20/30/50 per page).            */
/*                                                                     */
/*  DATA — wired to the real endpoints (no mock data):                 */
/*    O1  GET /api/v1/agency/certificates?status&search&sort_by&         */
/*        sort_order&limit&page   → { data: { certificates, total,      */
/*        limit, page, current_page, total_pages } }                     */
/*    O2  GET /api/v1/agency/certificates/{certificate_id}               */
/*        → full record, incl. pdf_download_url                          */
/*    PDF GET {pdf_download_url}  → application/pdf (needs the Bearer    */
/*        token, so it is fetched in JS and shown via an object URL)      */
/*                                                                     */
/*  Pagination is server-side now: `limit`/`page` are sent to O1, the    */
/*  rows returned ARE the current page (no client slicing), and the      */
/*  footer uses `total` / `total_pages`. `sort_by=issue_date` +          */
/*  `sort_order=desc` are sent explicitly so ordering is deterministic.  */
/*  `status` / `search` are left unset — the register shows every        */
/*  certificate, exactly as the UI did before.                           */
/*                                                                     */
/*  "View" keeps the PDF-only preview:                                   */
/*    - O2 is fetched for the full record, then its pdf_download_url is  */
/*      requested with the token and handed to the iframe as a blob URL  */
/*    - PDF embedded in an iframe (viewer toolbar hidden — no            */
/*      download / print controls)                                       */
/*    - the company LOGO watermark is overlaid diagonally on the         */
/*      preview (WATERMARK_URL — point it at your own logo asset)        */
/*    - context menu blocked inside the viewer                           */
/*    - no download link or button anywhere; object URL revoked on close */
/* ================================================================== */

/* URL of the watermark logo (served from /public in this demo).
   In the real app, point this at your own logo asset. */
const WATERMARK_URL = '/Ratified-Logo-Main.svg';

const PAGE_SIZES = [10, 20, 30, 50];

/* API sort defaults (no sort control in this UI, so the register always
   reads newest-issue-date first). */
const SORT_BY = 'issue_date';
const SORT_ORDER = 'desc';

/* ----------------------- response → row mapping --------------------- */
/* Maps an O1 list item or an O2 detail record onto the fields the table
   renders. O1 uses `certificate_id` / `issue_date`; O2 uses `id` /
   `verification_date`, so both shapes land on the same row object. */
const toRow = (row = {}) => {
  const customer = row.customer || {};
  const numericId = row.certificate_id ?? row.id ?? null;
  return {
    /* React key */
    key: numericId ?? row.certificate_number ?? null,
    /* numeric PK used to open the detail endpoint (O2) */
    certificateId: numericId,
    /* shown in the "Certificate ID" column */
    certificate_number: row.certificate_number || String(numericId ?? ''),
    customer_name: customer.full_name || row.customer_name || '—',
    customer_email: customer.email_address || row.email_address || '',
    customer_user_id: customer.user_id ?? row.customer_user_id ?? null,
    issued_on: row.issue_date ?? row.verification_date ?? null,
    expiry_date: row.expiry_date ?? null,
    /* { id, label } on both endpoints */
    status: row.status?.label || '',
    pdf_download_url: row.pdf_download_url || '',
  };
};

/* The register's `status` is the source of truth; the date check stays as
   the fallback for rows that come back without a status. */
const isActive = (row) => !row.status || row.status.toLowerCase() === 'active';
const expiredByDate = (row) =>
  row.expiry_date ? new Date(row.expiry_date) < new Date() : false;
const isFlagged = (row) => !isActive(row) || expiredByDate(row);
const flagLabel = (row) => (isActive(row) ? 'Expired' : row.status);

/* Re-point an absolute API URL at the configured BASE_URL, so a backend that
   builds URLs from its own origin (e.g. http://127.0.0.1:8000) still works
   through the app's proxy / deployed host. */
const toAppUrl = (url) => {
  if (!url) return '';
  if (!BASE_URL) return url;
  try {
    const parsed = new URL(url, window.location.origin);
    return `${String(BASE_URL).replace(/\/$/, '')}${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return url;
  }
};

/* Diagonal logo watermark grid for the PDF viewer overlay */
const WATERMARK_GRID = [
  { top: 15, left: 20 },
  { top: 15, left: 50 },
  { top: 15, left: 80 },
  { top: 50, left: 20 },
  { top: 50, left: 50 },
  { top: 50, left: 80 },
  { top: 85, left: 20 },
  { top: 85, left: 50 },
  { top: 85, left: 80 },
];

/* Compact page list: 1 … 4 5 6 … 12 */
const getPageItems = (totalPages, current) => {
  if (totalPages <= 7)
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  const items = new Set([
    1,
    2,
    totalPages - 1,
    totalPages,
    current - 1,
    current,
    current + 1,
  ]);
  const list = [...items]
    .filter((n) => n >= 1 && n <= totalPages)
    .sort((a, b) => a - b);
  const out = [];
  let prev = 0;
  list.forEach((n) => {
    if (n - prev > 1) out.push('…');
    out.push(n);
    prev = n;
  });
  return out;
};

const Certificates = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [certificates, setCertificates] = useState([]);
  /* pagination — server-side (the API owns total / total_pages) */
  const [total, setTotal] = useState(0);
  const [apiTotalPages, setApiTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  /* Certificate currently open in the PDF viewer (null = closed) */
  const [viewing, setViewing] = useState(null);
  const [pdf, setPdf] = useState({ url: '', loading: false, error: '' });

  const listRequestId = useRef(0);
  const viewerRequestId = useRef(0);
  const objectUrlRef = useRef('');

  const GetAPIcalls = useCallback(
    async (url) => {
      const token = localStorage.getItem('jwtToken');
      const apiUrl = `${BASE_URL}${url}`;
      const headers = {
        'Content-Type': 'application/json',
      };

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      try {
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers,
        });

        if (!response.ok) {
          if (response.status === 401) {
            localStorage.removeItem('jwtToken');
            navigate('/login');
            throw new Error('Session expired. Please log in again.');
          }
          const errorData = await response.json().catch(() => ({
            message: 'An unexpected error occurred',
            status: response.status,
          }));

          // Ensure errorData.message is extracted properly
          const errorMessage =
            typeof errorData === 'object' && errorData.message
              ? errorData.message
              : `Error ${response.status}: ${response.statusText}`;

          throw new Error(errorMessage);
        }

        return await response.json();
      } catch (error) {
        console.error('API Call Error:', error); // Log full error for debugging
        throw new Error(
          typeof error.message === 'string'
            ? error.message
            : 'Network error, please try again later',
        );
      }
    },
    [navigate],
  );

  /* ------------------- pagination derived from the API ------------------- */
  /* `total_pages` comes from O1; the ceil() is only a fallback for a
     response that omits it. `current` clamps the requested page so a
     shrinking register can never ask for a page that no longer exists. */
  const totalPages = Math.max(
    1,
    apiTotalPages || Math.ceil(total / pageSize) || 1,
  );
  const current = useMemo(
    () => Math.max(1, Math.min(page, totalPages)),
    [page, totalPages],
  );

  /* keep `page` in sync if a smaller result set shrinks the register */
  useEffect(() => {
    if (page !== current) setPage(current);
  }, [page, current]);

  const changePageSize = (n) => {
    setPageSize(n);
    setPage(1);
  };

  /* ----------------------------- O1: register ----------------------------- */
  const getCertificates = useCallback(async () => {
    const requestId = listRequestId.current + 1;
    listRequestId.current = requestId;
    setLoading(true);
    setError('');
    try {
      const query = new URLSearchParams({
        sort_by: SORT_BY,
        sort_order: SORT_ORDER,
        limit: String(pageSize),
        page: String(current),
      });
      const response = await GetAPIcalls(
        `/api/v1/agency/certificates?${query.toString()}`,
      );
      if (listRequestId.current !== requestId) return; // a newer page won

      if (response?.status === 200) {
        const payload = response.data || {};
        const rows = Array.isArray(payload.certificates)
          ? payload.certificates
          : [];
        setCertificates(rows.map(toRow));
        setTotal(Number(payload.total ?? rows.length));
        setApiTotalPages(Number(payload.total_pages ?? 1) || 1);
      } else {
        setCertificates([]);
        setTotal(0);
        setApiTotalPages(1);
        setError(response?.message || 'Could not load certificates.');
      }
    } catch (err) {
      if (listRequestId.current !== requestId) return;
      setCertificates([]);
      setTotal(0);
      setApiTotalPages(1);
      setError(
        err?.message || 'Could not load certificates. Please try again.',
      );
    } finally {
      if (listRequestId.current === requestId) setLoading(false);
    }
  }, [GetAPIcalls, current, pageSize]);

  useEffect(() => {
    getCertificates();
  }, [getCertificates]);

  /* ------------------------- PDF (authenticated) -------------------------- */
  /* pdf_download_url is an authenticated API URL, not a public link, so the
     bytes are fetched with the Bearer token and served to the <iframe> from a
     blob object URL (an iframe cannot send an Authorization header). */
  const fetchCertificatePdf = useCallback(
    async (url) => {
      const token = localStorage.getItem('jwtToken');
      const headers = { Accept: 'application/pdf' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(toAppUrl(url), { method: 'GET', headers });
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        throw new Error(
          `Could not open this certificate (Error ${response.status}).`,
        );
      }

      const blob = await response.blob();
      if (!blob || !blob.size) throw new Error('The certificate PDF is empty.');

      /* Re-declare the type as PDF so the browser's viewer renders it even if
         the API responds with a generic content type. */
      const typed = String(blob.type || '').includes('pdf')
        ? blob
        : blob.slice(0, blob.size, 'application/pdf');
      return URL.createObjectURL(typed);
    },
    [navigate],
  );

  const releasePdf = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = '';
    }
  }, []);

  /* ---------------------- View = O2 detail + PDF -------------------------- */
  const handleOpenViewer = useCallback(
    async (row) => {
      if (!row?.pdf_download_url && row?.certificateId == null) return;

      const requestId = viewerRequestId.current + 1;
      viewerRequestId.current = requestId;
      releasePdf();

      setViewing(row);
      setPdf({ url: '', loading: true, error: '' });

      try {
        /* the row already carries a pdf URL, but O2 is the detail read the
           register's certificate_id is meant to open — it returns the full
           record (customer, dates, status) and a fresh download URL. */
        let detail = row;
        if (row.certificateId != null) {
          try {
            const response = await GetAPIcalls(
              `/api/v1/agency/certificates/${row.certificateId}`,
            );
            if (response?.status === 200 && response?.data) {
              detail = toRow(response.data);
            }
          } catch {
            /* the register row already carries everything the viewer needs,
               so a failed detail read must not block the preview. */
            detail = row;
          }
          if (viewerRequestId.current !== requestId) return;
        }

        const downloadUrl = detail.pdf_download_url || row.pdf_download_url;
        if (!downloadUrl) {
          throw new Error('No certificate PDF is available yet.');
        }

        const objectUrl = await fetchCertificatePdf(downloadUrl);
        if (viewerRequestId.current !== requestId) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        objectUrlRef.current = objectUrl;
        setPdf({ url: objectUrl, loading: false, error: '' });
      } catch (err) {
        if (viewerRequestId.current !== requestId) return;
        setPdf({
          url: '',
          loading: false,
          error: err?.message || 'Could not open the certificate PDF.',
        });
      }
    },
    [GetAPIcalls, fetchCertificatePdf, releasePdf],
  );

  const handleCloseViewer = useCallback(() => {
    viewerRequestId.current += 1; // abandon any in-flight detail/PDF read
    releasePdf();
    setPdf({ url: '', loading: false, error: '' });
    setViewing(null);
  }, [releasePdf]);

  /* never leak a blob URL */
  useEffect(() => releasePdf, [releasePdf]);

  const start = total === 0 ? 0 : (current - 1) * pageSize;
  const end = Math.min(start + certificates.length, total);
  /* the API returns only the current page, so no client-side slicing */
  const pageRows = certificates;

  const formatDate = (value) => {
    if (!value) return '—';
    try {
      return new Date(value).toLocaleDateString('en-GB');
    } catch {
      return String(value);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-4 lg:p-6 lg:min-w-[78vw] lg:max-w-[80vw] h-full">
      <div className="w-full overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_1px_2px_rgba(15,23,42,.04),0_20px_48px_-32px_rgba(15,23,42,.35)]">
        {/* Header */}
        <div className="flex w-full flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-slate-100 px-4 py-3.5 sm:px-5">
          <div className="flex min-w-0 items-center gap-3">
            <span
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px]"
              style={{ background: '#375AE714', color: '#375AE7' }}
            >
              <svg
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M15 7a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h10z" />
                <path d="M15 7V5a2 2 0 0 1 2-2h2v4a2 2 0 0 1-2 2h-4" />
                <path d="M9 13h4" />
                <path d="M9 17h2" />
              </svg>
            </span>
            <div className="min-w-0 leading-tight">
              <h2 className="truncate text-[14.5px] font-bold tracking-tight text-slate-800">
                Certificates
              </h2>
              <p className="text-[11px] font-medium text-slate-400">
                Ratified customer certificates — PDF view only
              </p>
            </div>
          </div>
          <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-[5px] text-[10.5px] font-semibold text-slate-500">
            {total} certificate{total === 1 ? '' : 's'}
          </span>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse text-left">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/60">
                <th className="px-4 py-3 text-[10.5px] font-semibold uppercase tracking-[.06em] text-slate-500 sm:px-5">
                  Certificate ID
                </th>
                <th className="px-4 py-3 text-[10.5px] font-semibold uppercase tracking-[.06em] text-slate-500">
                  Customer Name
                </th>
                <th className="px-4 py-3 text-[10.5px] font-semibold uppercase tracking-[.06em] text-slate-500">
                  Issue Date
                </th>
                <th className="px-4 py-3 text-[10.5px] font-semibold uppercase tracking-[.06em] text-slate-500">
                  Expiry Date
                </th>
                <th className="px-4 py-3 text-right text-[10.5px] font-semibold uppercase tracking-[.06em] text-slate-500 sm:px-5">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={5} className="px-5 py-10 text-center">
                    <span className="inline-flex items-center gap-2 text-[13px] font-medium text-slate-400">
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-[#375AE7]" />
                      Loading certificates…
                    </span>
                  </td>
                </tr>
              )}
              {!loading && error && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-5 py-10 text-center text-[13px] font-medium text-red-600"
                  >
                    {error}
                  </td>
                </tr>
              )}
              {!loading && !error && total === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-5 py-10 text-center text-[13px] font-medium text-slate-400"
                  >
                    No certificates found.
                  </td>
                </tr>
              )}
              {!loading &&
                pageRows.map((c) => {
                  const canView =
                    Boolean(c.pdf_download_url) || c.certificateId != null;
                  const flagged = isFlagged(c);
                  return (
                    <tr
                      key={c.key ?? c.certificate_number}
                      className="border-b border-slate-100 transition-colors last:border-0 hover:bg-slate-50/60"
                    >
                      <td className="px-4 py-3.5 sm:px-5">
                        <span className="text-[13.5px] font-semibold text-slate-800">
                          {c.certificate_number}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="text-[13.5px] font-medium text-slate-700">
                          {c.customer_name}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="text-[13.5px] font-medium text-slate-700">
                          {formatDate(c.issued_on)}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="flex items-center gap-2">
                          <span
                            className={`text-[13.5px] font-medium ${
                              flagged ? 'text-red-600' : 'text-slate-700'
                            }`}
                          >
                            {formatDate(c.expiry_date)}
                          </span>
                          {flagged && (
                            <span className="rounded-full border border-red-200 bg-red-50 px-2 py-[2px] text-[10px] font-semibold uppercase tracking-wide text-red-600">
                              {flagLabel(c)}
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-right sm:px-5">
                        <span
                          role="button"
                          onClick={() => canView && handleOpenViewer(c)}
                          className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-bold transition-all active:scale-[0.97] ${
                            canView
                              ? 'border-[#375AE7]/30 bg-[#375AE7]/10 text-[#375AE7] hover:-translate-y-0.5 hover:bg-[#375AE7] hover:text-white hover:shadow-md hover:shadow-[#375AE7]/30'
                              : 'cursor-not-allowed border-slate-200 bg-slate-50 text-slate-300'
                          }`}
                        >
                          <svg
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                          View
                        </span>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && total > 0 && (
          <div className="flex w-full flex-wrap items-center justify-between gap-x-4 gap-y-3 border-t border-slate-100 px-4 py-3 sm:px-5">
            <span className="text-[11.5px] font-medium text-slate-400">
              Showing {start + 1}–{end} of {total} certificates
            </span>
            <div className="flex items-center gap-3">
              {/* rows per page */}
              <label className="flex items-center gap-1.5 text-[11.5px] font-medium text-slate-400">
                Rows per page
                <CustomSelect
                  value={pageSize}
                  options={PAGE_SIZES.map((size) => ({
                    value: size,
                    label: String(size),
                  }))}
                  onChange={(value) => changePageSize(Number(value))}
                  ariaLabel="Rows per page"
                  buttonClassName="h-auto rounded-lg px-2 py-1.5 text-[12px] font-semibold text-slate-600"
                />
              </label>

              {/* prev / next + page numbers */}
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setPage(Math.max(1, current - 1))}
                  disabled={current === 1}
                  aria-label="Previous page"
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition enabled:hover:border-[#375AE7]/40 enabled:hover:bg-[#375AE7]/5 enabled:hover:text-[#375AE7] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="m15 18-6-6 6-6" />
                  </svg>
                </button>
                {getPageItems(totalPages, current).map((item, idx) =>
                  item === '…' ? (
                    <span
                      key={`gap-${idx}`}
                      className="px-1 text-[12px] font-medium text-slate-300"
                    >
                      …
                    </span>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setPage(item)}
                      aria-current={item === current ? 'page' : undefined}
                      className={`h-8 min-w-8 rounded-lg px-2 text-[12px] font-bold transition ${
                        item === current
                          ? 'bg-[#375AE7] text-white shadow-sm shadow-[#375AE7]/30'
                          : 'text-slate-500 hover:bg-[#375AE7]/5 hover:text-[#375AE7]'
                      }`}
                    >
                      {item}
                    </button>
                  ),
                )}
                <button
                  type="button"
                  onClick={() => setPage(Math.min(totalPages, current + 1))}
                  disabled={current === totalPages}
                  aria-label="Next page"
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition enabled:hover:border-[#375AE7]/40 enabled:hover:bg-[#375AE7]/5 enabled:hover:text-[#375AE7] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* PDF-only viewer (no download) */}
      {viewing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4"
          onClick={handleCloseViewer}
          onContextMenu={(e) => e.preventDefault()}
        >
          <div
            className="flex h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            onContextMenu={(e) => e.preventDefault()}
          >
            {/* Viewer header */}
            <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 sm:px-5">
              <div className="flex min-w-0 items-center gap-3">
                <img
                  src={WATERMARK_URL}
                  alt=""
                  aria-hidden="true"
                  className="h-8 w-8 shrink-0 rounded-lg bg-slate-50 object-contain p-1 ring-1 ring-slate-100"
                />
                <div className="min-w-0 leading-tight">
                  <h3 className="truncate text-[13.5px] font-bold text-slate-800">
                    {viewing.certificate_number}
                  </h3>
                  <p className="truncate text-[11px] font-medium text-slate-400">
                    {viewing.customer_name} · Read Only
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleCloseViewer}
                className="shrink-0 rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                >
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* PDF embed + LOGO watermark overlay */}
            <div className="relative min-h-0 flex-1 bg-slate-100">
              {pdf.url && (
                <iframe
                  title={`certificate-${viewing.certificate_number}`}
                  src={`${pdf.url}#toolbar=0&navpanes=0&view=FitH`}
                  className="h-full w-full border-0"
                />
              )}
              {!pdf.url && (
                <div className="absolute inset-0 flex items-center justify-center px-6 text-center">
                  {pdf.loading ? (
                    <span className="inline-flex items-center gap-2 text-[13px] font-medium text-slate-400">
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-[#375AE7]" />
                      Loading certificate PDF…
                    </span>
                  ) : (
                    <span className="text-[13px] font-medium text-red-600">
                      {pdf.error}
                    </span>
                  )}
                </div>
              )}
              {/* Diagonal logo watermark — sits over the PDF */}
              <div className="pointer-events-none absolute inset-0 select-none overflow-hidden">
                {WATERMARK_GRID.map(({ top, left }) => (
                  <img
                    key={`${top}-${left}`}
                    src={WATERMARK_URL}
                    alt=""
                    aria-hidden="true"
                    className="absolute h-24 w-auto opacity-[0.10]"
                    style={{
                      top: `${top}%`,
                      left: `${left}%`,
                      transform: 'translate(-50%, -50%) rotate(-28deg)',
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Certificates;
